import type { Entity } from '@backstage/catalog-model';
import { isStackV1Entity } from './stack';
import type { StackCapability } from './stack';

/**
 * A tab a Stack page can carry beyond its own details.
 *
 * Named after the thing the tab needs to exist rather than after the plugin
 * that ships it, so a family declares what it is capable of and the plugins
 * ask, instead of every plugin carrying its own list of families.
 */
export type StackFamily = {
  id: string;
  label: string;
  /** Template tags that put a template in this family. */
  tags: string[];
  /** Template names known to belong to it. */
  templates: string[];
  capabilities: StackCapability[];
};

/**
 * What a template family actually is, and therefore what its page can offer.
 *
 * A bucket has no machine to open a console on and a VM has no bucket to give
 * a lifecycle policy to, so a page that offers both to both is offering a tab
 * that can only ever say "nothing here". The capability list is what makes the
 * difference visible to the plugins that contribute those tabs.
 *
 * Order matters for `tags`: the first match wins, so the specific tags come
 * before `vm`. Windows is split from Linux because the two ask different
 * questions of whoever orders them.
 */
export const STACK_FAMILIES: StackFamily[] = [
  {
    id: 'database',
    label: 'Managed database',
    tags: ['database'],
    templates: [],
    capabilities: ['console', 'graph', 'monitoring'],
  },
  {
    id: 'messaging',
    label: 'Message broker',
    tags: ['messaging'],
    templates: [],
    capabilities: ['console', 'graph', 'monitoring'],
  },
  {
    id: 'kubernetes',
    label: 'Kubernetes cluster',
    tags: ['kubernetes', 'cluster'],
    templates: ['k8s-cluster'],
    // No console: the cluster is reached with kubectl through the Capsule
    // proxy, not by opening a session on a machine the platform brokers.
    capabilities: ['graph'],
  },
  {
    id: 'volume',
    label: 'Block volume',
    // Deliberately before `storage`: cloudstack-volume ships the `storage` tag
    // too, and the first family whose tags match wins, so listing it after
    // Object storage would file every volume under buckets.
    tags: ['volume', 'block-storage'],
    templates: ['cloudstack-volume'],
    // No console and no monitoring: a volume is a disk, not a machine. There
    // is nothing to open a session on and no guest agent to scrape. Attach and
    // detach are day-two operations on the stack, not tabs.
    capabilities: [],
  },
  {
    id: 'storage',
    label: 'Object storage',
    tags: ['storage', 'object-storage', 's3'],
    templates: ['object-storage'],
    capabilities: ['storage-lifecycle'],
  },
  {
    id: 'network-rules',
    label: 'Network rules',
    // Before `network`: the firewall rule set and the VPC tier ACL also carry
    // the `network` tag, and filed there they would read as an address in the
    // template menu. Which tabs their pages carry is not decided here but by
    // the capabilities the IDP publishes for the stack type - none for either
    // (orchestrator internal/registry/catalog_capabilities_test.go): they are
    // policy on a network another order created.
    tags: ['firewall', 'acl'],
    templates: ['firewall-ruleset', 'cloudstack-tier-acl'],
    capabilities: [],
  },
  {
    id: 'network',
    label: 'Network address',
    tags: ['network', 'public-ip'],
    templates: ['public-ip'],
    capabilities: [],
  },
  {
    id: 'image',
    label: 'Machine image',
    tags: ['image'],
    templates: ['tenant-image'],
    capabilities: [],
  },
  {
    id: 'windows',
    label: 'Windows Server',
    tags: ['windows'],
    templates: ['windows-vm', 'azure-windows-vm'],
    capabilities: ['console', 'graph', 'monitoring'],
  },
  {
    id: 'compute',
    label: 'Linux VM',
    tags: ['vm'],
    templates: ['cloudstack-vm', 'azure-vm', 'vm-service'],
    capabilities: ['console', 'graph', 'monitoring'],
  },
];

export const OTHER_FAMILY: StackFamily = {
  id: 'other',
  label: 'Other',
  tags: [],
  templates: [],
  capabilities: ['console', 'graph', 'monitoring', 'storage-lifecycle'],
};

/** The family a template belongs to, from the tags the IDP ships with it. */
export function familyOfTags(tags: string[]): StackFamily {
  const set = new Set(tags.map(tag => tag.toLowerCase()));
  return (
    STACK_FAMILIES.find(family => family.tags.some(tag => set.has(tag))) ??
    OTHER_FAMILY
  );
}

/**
 * The family a provisioned Stack belongs to, from its canonical Stack type.
 *
 * Tags are the better signal but they live on the Template entity, and the
 * callers of this function have a Stack in hand and no way to fetch anything:
 * an entity filter is a synchronous predicate. So this resolves by name, and
 * returns undefined for a name it does not recognise - see `stackSupports` for
 * why that is deliberately not the same as "supports nothing".
 */
export function familyOfTemplateName(
  templateName: string | undefined,
): StackFamily | undefined {
  if (!templateName) {
    return undefined;
  }
  const name = templateName.toLowerCase();
  return STACK_FAMILIES.find(family => family.templates.includes(name));
}

/**
 * Whether a component provisioned from `templateName` can carry `capability`.
 *
 * The name-only form, for callers that have a template name rather than an
 * entity in hand. An unrecognised name is answered the same way `stackSupports`
 * answers it: yes, because a family the IDP ships after this file was written
 * must not silently lose a tab.
 */
export function templateSupports(
  templateName: string | undefined,
  capability: StackCapability,
): boolean {
  const family = familyOfTemplateName(templateName);
  return !family || family.capabilities.includes(capability);
}

/** The family of a Stack entity, or undefined if it cannot be told. */
export function familyOfStackEntity(entity: Entity): StackFamily | undefined {
  return isStackV1Entity(entity)
    ? familyOfTemplateName(entity.spec.stackType)
    : undefined;
}

export function isStackEntity(entity: Entity): boolean {
  return isStackV1Entity(entity);
}

/**
 * Whether a Stack's page should carry the tab behind `capability`.
 *
 * An unrecognised template gets every tab rather than none. The IDP ingests
 * new template families without anybody editing this file, and a family
 * shipped tomorrow losing its Logs tab today would be a far worse failure than
 * a tab that turns out to have nothing to show.
 */
export function stackSupports(
  entity: Entity,
  capability: StackCapability,
): boolean {
  if (!isStackEntity(entity)) {
    return false;
  }
  return isStackV1Entity(entity)
    ? entity.spec.capabilities?.includes(capability) === true
    : false;
}
